import { z } from 'zod';

const uuidSchema = z.string().uuid();
const currencySchema = z.string().regex(/^[A-Z]{3}$/, 'currency must be an ISO 4217 code');
const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must use YYYY-MM-DD').refine((value) => {
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, 'date must be a valid calendar date');
const datetimeSchema = z.string().refine((value) => {
  if (!/(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return false;
  return dateSchema.safeParse(value.slice(0, 10)).success && !Number.isNaN(Date.parse(value));
}, 'datetime must be timezone-aware');
const decimalSchema = z.string().regex(/^-?(0|[1-9][0-9]{0,14})\.[0-9]{4}$/, 'money must use scale 4')
  .refine((value) => value !== '-0.0000', 'negative zero is not allowed');
const aggregateDecimalSchema = z.string().regex(/^-?(0|[1-9][0-9]*)\.[0-9]{4}$/, 'aggregate must use scale 4')
  .refine((value) => value !== '-0.0000', 'negative zero is not allowed');
const displayTextSchema = z.string().min(1).max(200);
const safeTextSchema = z.string().min(1).max(160);

const accountSummarySchema = z.object({
  account_id: uuidSchema,
  display_name: displayTextSchema,
  last4: z.string().min(1).max(4).nullable(),
}).strict();

const degradedSourceSchema = z.object({
  label: safeTextSchema,
  status: safeTextSchema,
}).strict();

const effectiveScopeSchema = z.object({
  date_from: dateSchema.nullable(),
  date_to: dateSchema.nullable(),
  accounts: z.array(accountSummarySchema).max(50),
  saved_timezone: z.string().min(1).max(64),
  degraded_sources: z.array(degradedSourceSchema),
}).strict();

const interpretationSchema = z.object({
  window: z.object({
    start: dateSchema,
    end: dateSchema,
    clustered: z.boolean(),
  }).strict().nullable(),
  account_last4s: z.array(z.string().min(1).max(4)),
  event_name: displayTextSchema.nullable(),
  categories: z.array(displayTextSchema).max(50),
  location: displayTextSchema.nullable(),
  merchant_labels: z.array(displayTextSchema).max(50),
  amount_min: decimalSchema.nullable(),
  amount_max: decimalSchema.nullable(),
  suggested_event: z.object({
    name: displayTextSchema,
    kind: z.enum(['trip', 'project', 'other']),
    start_date: dateSchema,
    end_date: dateSchema,
    location: displayTextSchema.nullable(),
    tag_business: z.boolean(),
  }).strict().nullable(),
  condition_summary: z.object({
    name: displayTextSchema,
    enabled: z.boolean(),
  }).strict().nullable(),
}).strict().superRefine((value, context) => {
  if (value.window && value.window.start > value.window.end) {
    context.addIssue({ code: 'custom', path: ['window'], message: 'window dates are reversed' });
  }
  if (value.amount_min && value.amount_max && value.amount_min > value.amount_max) {
    context.addIssue({ code: 'custom', path: ['amount_min'], message: 'amount range is reversed' });
  }
});

const coverageSchema = z.object({
  state: z.enum(['complete', 'partial_source', 'unavailable']),
  partial_source: z.boolean(),
  label: safeTextSchema.nullable(),
}).strict().superRefine((value, context) => {
  if (value.partial_source !== (value.state === 'partial_source')) {
    context.addIssue({ code: 'custom', path: ['partial_source'], message: 'partial_source does not match coverage state' });
  }
});

const categorySchema = z.object({
  category_id: uuidSchema.nullable(),
  name: displayTextSchema.nullable(),
  status: z.enum(['current', 'archived', 'none']),
  taxonomy_version: z.string().nullable(),
}).strict();

const reviewRowSchema = z.object({
  line_item_id: uuidSchema,
  version: z.number().int().positive(),
  canonical_transaction_id: uuidSchema,
  merchant: displayTextSchema,
  account: accountSummarySchema,
  posted_day: dateSchema,
  pending: z.boolean(),
  amount: decimalSchema,
  currency: currencySchema,
  category: categorySchema,
  business_state: z.enum(['unreviewed', 'business', 'personal', 'mixed']),
  event: z.object({ event_id: uuidSchema, name: displayTextSchema }).strict().nullable(),
  review_bucket: z.enum(['exact', 'likely_related']),
  bank_state: z.enum(['live_canonical_tip', 'retained_annotation_anchor', 'non_writable']),
  write_context: z.string().min(1).max(2048).nullable(),
}).strict();

const analyticalResultSchema = z.object({
  kind: z.enum(['trend', 'breakdown']),
  result_ref: z.string().min(1).max(1024),
  availability: z.enum(['current', 'partial', 'rebuilding', 'failed', 'no_data']),
  accessible_summary: displayTextSchema,
  total: aggregateDecimalSchema.nullable(),
  currency: currencySchema.nullable(),
}).strict().superRefine((value, context) => {
  if ((value.total === null) !== (value.currency === null)) {
    context.addIssue({ code: 'custom', path: ['total'], message: 'total and currency must be paired' });
  }
});

export const askDraftSchema = z.object({
  draft_id: uuidSchema,
  sentence: z.string().min(1).max(1000),
  state: z.enum(['previewing', 'preview_ready', 'no_match', 'unsupported', 'needs_narrower_scope', 'partial_source', 'failed', 'committing', 'changed_since', 'committed', 'discarded', 'expired']),
  created_at: datetimeSchema,
  expires_at: datetimeSchema,
  effective_scope: effectiveScopeSchema,
  intent: z.object({ kind: z.enum(['filter', 'event', 'condition', 'event_and_condition', 'trend', 'breakdown', 'unsupported']) }).strict(),
  interpretation: interpretationSchema.nullable(),
  supported_outcomes: z.array(z.enum(['event', 'condition', 'event_and_condition'])).max(3),
  coverage: coverageSchema,
  match_count: z.number().int().nonnegative().nullable(),
  known_match_count: z.number().int().nonnegative(),
  known_currency_totals: z.record(currencySchema, aggregateDecimalSchema).nullable(),
  exclusions: z.array(z.object({ reason: safeTextSchema, count: z.number().int().positive() }).strict()).max(200),
  new_matches_since_preview: z.number().int().nonnegative(),
  review_page: z.object({
    cursor: z.string().max(1024).nullable(),
    next_cursor: z.string().max(1024).nullable(),
    limit: z.number().int().min(1).max(200),
  }).strict().nullable(),
  review_rows: z.array(reviewRowSchema).max(200),
  analytical_result: analyticalResultSchema.nullable(),
  message: safeTextSchema.nullable(),
  retry_action: safeTextSchema.nullable(),
}).strict().superRefine((value, context) => {
  if (value.expires_at <= value.created_at) {
    context.addIssue({ code: 'custom', path: ['expires_at'], message: 'expires_at must be after created_at' });
  }
  if (value.coverage.state === 'complete' && (value.match_count === null || value.match_count !== value.known_match_count)) {
    context.addIssue({ code: 'custom', path: ['match_count'], message: 'complete coverage requires authoritative match count' });
  }
  if (value.coverage.state === 'partial_source' && (value.match_count !== null || value.known_currency_totals === null)) {
    context.addIssue({ code: 'custom', path: ['coverage'], message: 'partial coverage requires known totals and unknown count' });
  }
  if (value.coverage.state === 'unavailable' && (value.match_count !== null || value.known_currency_totals !== null || value.supported_outcomes.length > 0)) {
    context.addIssue({ code: 'custom', path: ['coverage'], message: 'unavailable coverage cannot expose writable totals or outcomes' });
  }
  if (value.state === 'needs_narrower_scope' && (value.review_rows.length > 0 || value.supported_outcomes.length > 0)) {
    context.addIssue({ code: 'custom', path: ['state'], message: 'narrower scope cannot expose writable outcomes' });
  }
  if (new Set(value.supported_outcomes).size !== value.supported_outcomes.length) {
    context.addIssue({ code: 'custom', path: ['supported_outcomes'], message: 'supported outcomes must be unique' });
  }
  if (['expired', 'discarded', 'committed'].includes(value.state) && value.supported_outcomes.length > 0) {
    context.addIssue({ code: 'custom', path: ['state'], message: 'terminal drafts cannot expose writable outcomes' });
  }
});

export type AskDraft = z.infer<typeof askDraftSchema>;
export type AskReviewRow = z.infer<typeof reviewRowSchema>;
export type AskAnalyticalResult = z.infer<typeof analyticalResultSchema>;

// The hook modules use the data schema after `parseEnvelope`, matching the
// existing frontend API convention. The names are frozen for callers/tests.
export const askPreviewResponseSchema = askDraftSchema;
export const askDraftResponseSchema = askDraftSchema;
